import React, { useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Plus, Search, Trash2 } from 'lucide-react';
import { useUIColors } from '../../hooks/useTheme';
import { funnelStagePresentation } from './funnelStageUtils';

const Stacked = ({ top, bottom, topBold = true, topColor, bottomColor, mutedColor }) => (
  <div style={{ lineHeight: 1.25 }}>
    <div style={{ fontWeight: topBold ? 700 : 500, fontSize: '12px', color: topColor || '#0f172a' }}>{top || '—'}</div>
    {bottom ? (
      <div style={{ fontSize: '11px', color: bottomColor || mutedColor || '#64748b', marginTop: '1px' }}>{bottom}</div>
    ) : null}
  </div>
);

const contactStatusPresentation = (statusRaw, isDarkMode) => {
  const s = String(statusRaw || '')
    .trim()
    .toLowerCase();
  const base = { label: statusRaw && String(statusRaw).trim() ? String(statusRaw).trim() : '—' };
  if (s === 'activo') {
    return {
      ...base,
      bg: isDarkMode ? '#14532d' : '#dcfce7',
      color: isDarkMode ? '#bbf7d0' : '#166534',
      border: isDarkMode ? '#22c55e' : '#86efac',
    };
  }
  if (s === 'inactivo') {
    return {
      ...base,
      bg: isDarkMode ? '#3f3f46' : '#f4f4f5',
      color: isDarkMode ? '#d4d4d8' : '#52525b',
      border: isDarkMode ? '#52525b' : '#d4d4d8',
    };
  }
  return {
    ...base,
    bg: isDarkMode ? '#27272a' : '#f0ebe3',
    color: isDarkMode ? '#e4e4e7' : '#44403c',
    border: isDarkMode ? '#3f3f46' : '#d6d0c4',
  };
};

const TABLE_COLUMNS_FULL = [
  { key: 'Código', align: 'left', sortKey: 'contact_code' },
  { key: 'Nombre', align: 'left', sortKey: 'full_name' },
  { key: 'Contacto', align: 'left', sortKey: 'email' },
  { key: 'Organización', align: 'left', sortKey: 'organization_name' },
  { key: 'LinkedIn', align: 'left', sortKey: 'linkedin' },
  { key: 'Etapa funnel', align: 'center', sortKey: 'funnel_stage' },
  { key: 'Estado', align: 'center', sortKey: 'status' },
];

const TABLE_COLUMNS_COMPACT = [
  { key: 'Código', align: 'left', sortKey: 'contact_code' },
  { key: 'Nombre', align: 'left', sortKey: 'full_name' },
  { key: 'Contacto', align: 'left', sortKey: 'email' },
  { key: 'LinkedIn', align: 'left', sortKey: 'linkedin' },
  { key: 'Etapa funnel', align: 'center', sortKey: 'funnel_stage' },
  { key: 'Estado', align: 'center', sortKey: 'status' },
];

const getSortValue = (row, sortKey) => {
  switch (sortKey) {
    case 'contact_code':
      return String(row.contact_code || '').toLowerCase();
    case 'full_name':
      return String(row.full_name || '').toLowerCase();
    case 'email':
      return String(row.email || row.phone || '').toLowerCase();
    case 'organization_name':
      return String(row.organization_name || '').toLowerCase();
    case 'linkedin':
      return String(row.linkedin || '').toLowerCase();
    case 'funnel_stage':
      return String(row.funnel_stage || '').toLowerCase();
    case 'status':
      return String(row.status || '').toLowerCase();
    default:
      return '';
  }
};

const compareRows = (a, b, sortKey, sortDir) => {
  const mult = sortDir === 'asc' ? 1 : -1;
  const va = getSortValue(a, sortKey);
  const vb = getSortValue(b, sortKey);
  return String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' }) * mult;
};

/**
 * Tabla de contactos (A002_contacts): mismo look que Table3 de organizaciones.
 */
const ContactsTable3 = ({
  rows = [],
  loading = false,
  error = '',
  isDarkMode = false,
  tableTitle = 'Contactos',
  addButtonLabel = 'Nuevo contacto',
  subtitleOverride,
  /** Oculta columna/filtro de organización (p. ej. modal de una sola empresa). */
  hideOrganizationColumn = false,
  /** Resalta la fila del contacto activo (p. ej. para filtrar actividades). */
  activeRowId = null,
  onAdd,
  onRowNameClick,
  onRowClick,
  onBulkDelete,
}) => {
  const ui = useUIColors();
  const bg = ui.page;
  const headerBg = ui.header;
  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const rowHover = ui.rowHover;
  const padV = 9;
  const padH = 10;
  const thPadV = 8;
  const inputBg = ui.inputBg;
  const inputBorder = ui.inputBorder;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterOrgLink, setFilterOrgLink] = useState('');
  const [sortColumn, setSortColumn] = useState('full_name');
  const [sortDir, setSortDir] = useState('asc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkPending, setBulkPending] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const headerSelectRef = useRef(null);

  const tableColumns = hideOrganizationColumn ? TABLE_COLUMNS_COMPACT : TABLE_COLUMNS_FULL;
  const showBulkChrome = Boolean(onBulkDelete);
  const colCount = tableColumns.length + (showBulkChrome ? 1 : 0);

  const statusOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const st = String(r.status || '').trim();
      if (st) map.set(st, st);
    });
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b, 'es'));
  }, [rows]);

  const filteredRows = useMemo(() => {
    let list = rows;
    const q = searchQuery.trim().toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);
    if (tokens.length) {
      list = list.filter((row) => {
        const hay = [
          row.contact_code,
          row.full_name,
          row.role,
          row.email,
          row.phone,
          row.linkedin,
          row.organization_name,
          row.status,
        ]
          .filter((x) => x !== undefined && x !== null)
          .map((x) => String(x))
          .join(' ')
          .toLowerCase();
        return tokens.every((t) => hay.includes(t));
      });
    }
    if (filterStatus) {
      list = list.filter((row) => String(row.status || '').trim() === filterStatus);
    }
    if (!hideOrganizationColumn) {
      if (filterOrgLink === 'linked') {
        list = list.filter((row) => Number(row.organization_id) > 0 && String(row.organization_name || '').trim());
      } else if (filterOrgLink === 'orphan') {
        list = list.filter(
          (row) => !(Number(row.organization_id) > 0 && String(row.organization_name || '').trim())
        );
      }
    }
    return list;
  }, [rows, searchQuery, filterStatus, filterOrgLink, hideOrganizationColumn]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => compareRows(a, b, sortColumn, sortDir));
    return arr;
  }, [filteredRows, sortColumn, sortDir]);

  const allVisibleSelected =
    sortedRows.length > 0 && sortedRows.every((row) => selectedIds.includes(Number(row.id)));
  const someVisibleSelected = sortedRows.some((row) => selectedIds.includes(Number(row.id)));

  React.useEffect(() => {
    if (headerSelectRef.current) {
      headerSelectRef.current.indeterminate = someVisibleSelected && !allVisibleSelected;
    }
  }, [someVisibleSelected, allVisibleSelected]);

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      const visible = new Set(sortedRows.map((r) => Number(r.id)));
      setSelectedIds((prev) => prev.filter((id) => !visible.has(id)));
    } else {
      const visibleIds = sortedRows.map((r) => Number(r.id));
      setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const toggleSelectOne = (id) => {
    const nid = Number(id);
    setSelectedIds((prev) => (prev.includes(nid) ? prev.filter((x) => x !== nid) : [...prev, nid]));
  };

  const handleBulkDelete = async () => {
    if (!onBulkDelete || selectedIds.length === 0) return;
    const n = selectedIds.length;
    const ok = window.confirm(
      `¿Eliminar ${n} contacto${n === 1 ? '' : 's'} seleccionado${n === 1 ? '' : 's'}? Se marcarán como inactivos.`
    );
    if (!ok) return;
    setBulkPending(true);
    setBulkError('');
    try {
      await onBulkDelete(selectedIds);
      setSelectedIds([]);
    } catch (e) {
      setBulkError(e?.message || 'No se pudo eliminar.');
    } finally {
      setBulkPending(false);
    }
  };

  const handleSortClick = (key) => {
    if (sortColumn === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(key);
      setSortDir('asc');
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('');
    setFilterOrgLink('');
  };

  const hasActiveFilters =
    Boolean(searchQuery.trim()) ||
    Boolean(filterStatus) ||
    (!hideOrganizationColumn && Boolean(filterOrgLink));

  const subtitle =
    subtitleOverride != null
      ? subtitleOverride
      : loading
        ? 'Cargando…'
        : error ||
          (hasActiveFilters
            ? `Mostrando ${sortedRows.length} de ${rows.length} contactos · A002_contacts`
            : `${rows.length} contactos · A002_contacts`);

  const selectStyle = {
    height: '36px',
    padding: '0 10px',
    borderRadius: '8px',
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: '13px',
    minWidth: '140px',
    cursor: 'pointer',
    outline: 'none',
  };

  const SortIcon = ({ columnKey }) => {
    if (sortColumn !== columnKey) return <ArrowUpDown size={12} color={muted} />;
    return sortDir === 'asc' ? <ArrowUp size={12} color={text} /> : <ArrowDown size={12} color={text} />;
  };

  return (
    <div
      style={{
        border: `1px solid ${border}`,
        borderRadius: '12px',
        overflow: 'hidden',
        background: bg,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: `1px solid ${border}`,
          background: headerBg,
          flexWrap: 'wrap',
          gap: '10px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: text }}>{tableTitle}</h3>
          <p style={{ margin: 0, fontSize: '12px', color: muted }}>{subtitle}</p>
        </div>
        {onAdd ? (
          <button
            type="button"
            onClick={onAdd}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              height: '36px',
              padding: '0 14px',
              borderRadius: '8px',
              border: 'none',
              background: '#16a34a',
              color: '#fff',
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            <Plus size={16} /> {addButtonLabel}
          </button>
        ) : null}
      </div>

      <div
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${border}`,
          background: bg,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'flex-end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 220px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Buscar
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Search size={16} color={muted} aria-hidden />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Nombre, email, teléfono, organización…"
              style={{ ...selectStyle, flex: 1, minWidth: 0, cursor: 'text' }}
            />
          </span>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '140px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Estado
          </span>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={selectStyle}>
            <option value="">Todos</option>
            {statusOptions.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>
        </label>
        {!hideOrganizationColumn ? (
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '180px' }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
              Organización
            </span>
            <select value={filterOrgLink} onChange={(e) => setFilterOrgLink(e.target.value)} style={selectStyle}>
              <option value="">Todos</option>
              <option value="linked">Con organización</option>
              <option value="orphan">Sin organización</option>
            </select>
          </label>
        ) : null}
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            style={{
              height: '36px',
              padding: '0 12px',
              borderRadius: '8px',
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: text,
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            Limpiar
          </button>
        ) : null}
      </div>

      {showBulkChrome && selectedIds.length > 0 ? (
        <div
          style={{
            padding: '10px 16px',
            borderBottom: `1px solid ${border}`,
            background: isDarkMode ? '#1c1917' : '#fafaf9',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: '13px', color: text, fontWeight: 600 }}>
            {selectedIds.length} seleccionado{selectedIds.length === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            disabled={bulkPending}
            onClick={handleBulkDelete}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              height: '32px',
              padding: '0 12px',
              borderRadius: '6px',
              border: 'none',
              background: '#dc2626',
              color: '#fff',
              fontWeight: 600,
              fontSize: '12px',
              cursor: bulkPending ? 'wait' : 'pointer',
              opacity: bulkPending ? 0.7 : 1,
            }}
          >
            <Trash2 size={14} /> Eliminar
          </button>
          {bulkError ? <span style={{ fontSize: '12px', color: '#dc2626' }}>{bulkError}</span> : null}
        </div>
      ) : null}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '780px' }}>
          <thead>
            <tr style={{ background: headerBg, borderBottom: `1px solid ${border}` }}>
              {showBulkChrome ? (
                <th style={{ padding: `${thPadV}px ${padH}px`, width: 36, textAlign: 'center' }}>
                  <input
                    ref={headerSelectRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    aria-label="Seleccionar todos"
                  />
                </th>
              ) : null}
              {tableColumns.map((col) => (
                <th
                  key={col.key}
                  style={{
                    padding: `${thPadV}px ${padH}px`,
                    textAlign: col.align,
                    fontSize: '11px',
                    fontWeight: 700,
                    color: muted,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  onClick={() => handleSortClick(col.sortKey)}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {col.key}
                    <SortIcon columnKey={col.sortKey} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: muted }}>
                  Cargando contactos…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: '#dc2626' }}>
                  {error}
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: muted }}>
                  No hay contactos para mostrar.
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const statusStyle = contactStatusPresentation(row.status, isDarkMode);
                const funnelStyle = funnelStagePresentation(row.funnel_stage, isDarkMode);
                const orgName = String(row.organization_name || '').trim();
                const hasOrg = Number(row.organization_id) > 0 && orgName;
                const selected = selectedIds.includes(Number(row.id));
                const isActive = activeRowId != null && Number(row.id) === Number(activeRowId);
                const rowBg = selected || isActive ? rowHover : bg;
                return (
                  <tr
                    key={row.id}
                    style={{
                      borderBottom: `1px solid ${border}`,
                      background: rowBg,
                      cursor: onRowClick ? 'pointer' : undefined,
                      boxShadow: isActive ? `inset 3px 0 0 ${ui.link || '#2563eb'}` : undefined,
                    }}
                    onClick={() => onRowClick?.(row)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = rowHover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = rowBg;
                    }}
                  >
                    {showBulkChrome ? (
                      <td
                        style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleSelectOne(row.id)}
                          aria-label={`Seleccionar ${row.full_name || row.contact_code}`}
                        />
                      </td>
                    ) : null}
                    <td style={{ padding: `${padV}px ${padH}px`, color: muted, fontSize: '12px' }}>
                      {row.contact_code || '—'}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px` }}>
                      {onRowNameClick ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRowNameClick(row);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          <Stacked
                            top={row.full_name || '—'}
                            bottom={row.role || null}
                            topColor={ui.link || '#2563eb'}
                            mutedColor={muted}
                          />
                        </button>
                      ) : (
                        <Stacked
                          top={row.full_name || '—'}
                          bottom={row.role || null}
                          topColor={text}
                          mutedColor={muted}
                        />
                      )}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px` }}>
                      <Stacked top={row.email || '—'} bottom={row.phone || null} topColor={text} mutedColor={muted} />
                    </td>
                    {!hideOrganizationColumn ? (
                      <td style={{ padding: `${padV}px ${padH}px` }}>
                        <span
                          style={{
                            fontSize: '12px',
                            fontWeight: hasOrg ? 500 : 600,
                            color: hasOrg ? text : '#b45309',
                          }}
                        >
                          {hasOrg ? orgName : 'Sin organización'}
                        </span>
                      </td>
                    ) : null}
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: '12px', color: muted, maxWidth: 180 }}>
                      {row.linkedin ? (
                        <a
                          href={
                            /^https?:\/\//i.test(String(row.linkedin))
                              ? row.linkedin
                              : `https://${String(row.linkedin).replace(/^\/+/, '')}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: ui.link || '#2563eb', textDecoration: 'none' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {String(row.linkedin).replace(/^https?:\/\/(www\.)?/i, '').slice(0, 36)}
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: '999px',
                          fontSize: '11px',
                          fontWeight: 600,
                          background: funnelStyle.bg,
                          color: funnelStyle.color,
                          border: `1px solid ${funnelStyle.border}`,
                        }}
                      >
                        {funnelStyle.label}
                      </span>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: '999px',
                          fontSize: '11px',
                          fontWeight: 600,
                          background: statusStyle.bg,
                          color: statusStyle.color,
                          border: `1px solid ${statusStyle.border}`,
                        }}
                      >
                        {statusStyle.label}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ContactsTable3;
